/**
 * AnalyseCompleteAtsSection — the fused "Analyse ATS" section of the ATS
 * sidebar. Combines:
 * - Real-time preview score (`previewResult`) computed on every CV edit
 *   by useCvFileAnalysis.
 * - Full file-upload analysis (`completeAnalysisResult`) with
 *   precision/recall/F1, 5 scoring dimensions, field-level metrics,
 *   industry comparison, red/yellow/green flags, error analysis,
 *   extracted text preview, and an "AI parse to fill CV" button.
 *
 * Pure UI component — all state and handlers come from the parent.
 */

import { memo } from 'react';
import {
  AlertCircle,
  ChartNoAxesCombined,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Loader2,
  Sparkles,
  Upload,
  XCircle,
} from 'lucide-react';
import type { getTType } from '../../contexts/I18nContext';
import type {
  ATSPreviewResult,
  ATSCompleteAnalysisResult,
} from '../../lib/ats-analyzer';

export interface AnalyseCompleteAtsSectionProps {
  t: getTType;

  // Section expand/collapse
  isExpanded: boolean;
  onToggleExpanded: () => void;

  // Real-time preview (while editing)
  previewResult: ATSPreviewResult | null;

  // Full file-upload analysis
  completeAnalysisResult: ATSCompleteAnalysisResult | null;
  isAnalyzingFile: boolean;
  showCompleteAnalysis: boolean;
  setShowCompleteAnalysis: (next: boolean) => void;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void | Promise<void>;

  // AI parse-to-CV (from the extracted text)
  lastExtractedText: string;
  isAiParsing: boolean;
  onAiParseClick: () => void | Promise<void>;
}

export const AnalyseCompleteAtsSection = memo(function AnalyseCompleteAtsSection(props: AnalyseCompleteAtsSectionProps) {
  const {
    t,
    isExpanded,
    onToggleExpanded,
    previewResult,
    completeAnalysisResult,
    isAnalyzingFile,
    showCompleteAnalysis,
    setShowCompleteAnalysis,
    onFileUpload,
    lastExtractedText,
    isAiParsing,
    onAiParseClick,
  } = props;

  return (
    <div className="space-y-4">
      <h2
        className="flex items-center justify-between gap-2 text-base font-semibold text-white cursor-pointer hover:text-blue-300 transition-colors border-t border-white/10 pt-4"
        onClick={onToggleExpanded}
      >
        <span className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-600/20 flex items-center justify-center text-blue-400">
            <ChartNoAxesCombined size={18} />
          </div>
          Analyse ATS
        </span>
        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </h2>

      {isExpanded && (
        <div className="space-y-4">
          {/* === Real-time preview score === */}
          <div className="bg-white/5 rounded-xl border border-white/10 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <span className="text-sm text-white/80 font-semibold">
                  {t('ats.realtimeScore')}
                </span>
                <span className="text-sm text-white/40">{t('ats.whileEditing')}</span>
              </div>
              <span
                className={`text-2xl font-black ${
                  (previewResult?.score || 0) >= 70
                    ? 'text-green-400'
                    : (previewResult?.score || 0) >= 50
                      ? 'text-yellow-400'
                      : 'text-red-400'
                }`}
              >
                {previewResult?.score || 0}/100
              </span>
            </div>

            {previewResult && (
              <div className="space-y-2">
                <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1 scrollbar-hide">
                  {previewResult.feedback.map((f, i) => (
                    <div
                      key={i}
                      className="flex gap-2 items-start p-2 bg-white/5 rounded-lg border border-white/5"
                    >
                      {f.type === 'success' && (
                        <CheckCircle2 size={12} className="text-green-400 mt-0.5 shrink-0" />
                      )}
                      {f.type === 'warning' && (
                        <AlertCircle size={12} className="text-yellow-400 mt-0.5 shrink-0" />
                      )}
                      {f.type === 'error' && (
                        <XCircle size={12} className="text-red-400 mt-0.5 shrink-0" />
                      )}
                      <div>
                        <p className="text-sm text-white/70 leading-tight">{f.message}</p>
                        {f.criterion && (
                          <span className="text-sm text-white/30">{f.criterion}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {previewResult.missingKeywords.length > 0 && (
                  <div className="p-2 bg-blue-500/10 rounded-lg border border-blue-500/20">
                    <p className="text-sm text-blue-400 uppercase font-bold mb-1">
                      {t('ats.missingKeywords')}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {previewResult.missingKeywords.slice(0, 10).map((kw, i) => (
                        <span
                          key={i}
                          className="text-sm bg-blue-500/20 text-blue-300 px-1.5 py-0.5 rounded font-bold"
                        >
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* === Full file-upload analysis === */}
          <div className="bg-gradient-to-br from-green-500/5 to-emerald-500/5 rounded-xl border border-green-500/20 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <span className="text-sm text-white/80 font-semibold">
                  {t('ats.fullAnalysis')}
                </span>
                <span className="text-sm text-white/40">{t('ats.loadPdfDocx')}</span>
              </div>
              {showCompleteAnalysis && (
                <button
                  onClick={() => setShowCompleteAnalysis(false)}
                  className="text-sm text-white/40 hover:text-white/60 bg-white/5 px-2 py-1 rounded"
                >
                  Fermer
                </button>
              )}
            </div>

            <label className="flex items-center justify-center gap-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white px-4 py-3 rounded-xl text-sm font-black transition-all cursor-pointer shadow-lg shadow-green-600/20 w-full">
              {isAnalyzingFile ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  <span>Analyse en cours...</span>
                </>
              ) : (
                <>
                  <Upload size={14} />
                  <span>CHARGER PDF ou DOCX</span>
                </>
              )}
              <input
                type="file"
                className="hidden"
                accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={onFileUpload}
                disabled={isAnalyzingFile}
              />
            </label>

            {showCompleteAnalysis && completeAnalysisResult && (
              <div className="space-y-3 pt-2 max-h-[70vh] overflow-y-auto scrollbar-hide">
                {/* === VALIDATION STATUS === */}
                <div
                  className={`p-3 rounded-xl border ${
                    completeAnalysisResult.validationStatus === 'production_ready'
                      ? 'bg-green-500/10 border-green-500/30'
                      : completeAnalysisResult.validationStatus === 'acceptable'
                        ? 'bg-yellow-500/10 border-yellow-500/30'
                        : 'bg-red-500/10 border-red-500/30'
                  }`}
                >
                  <p className="text-sm font-bold">{completeAnalysisResult.validationMessage}</p>
                  <p className="text-sm text-white/50 mt-1">
                    {completeAnalysisResult.fileType.toUpperCase()} •{' '}
                    {completeAnalysisResult.parsingSuccess ? 'Parsing OK' : 'Parsing partiel'}
                  </p>
                </div>

                {/* === PRECISION / RECALL / F1 === */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 p-3 rounded-xl border border-blue-500/20 text-center">
                    <p className="text-[7px] text-blue-400 uppercase font-bold">Precision</p>
                    <p className="text-xl font-black text-blue-400">
                      {completeAnalysisResult.precision}%
                    </p>
                    <p className="text-[7px] text-white/30">Quand j'extrais, ai-je raison?</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 p-3 rounded-xl border border-purple-500/20 text-center">
                    <p className="text-[7px] text-purple-400 uppercase font-bold">Recall</p>
                    <p className="text-xl font-black text-purple-400">
                      {completeAnalysisResult.recall}%
                    </p>
                    <p className="text-[7px] text-white/30">Trouve-je tout?</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-500/10 to-emerald-600/10 p-3 rounded-xl border border-green-500/20 text-center">
                    <p className="text-[7px] text-green-400 uppercase font-bold">F1-Score</p>
                    <p className="text-xl font-black text-green-400">
                      {Math.round(
                        (2 *
                          (completeAnalysisResult.precision * completeAnalysisResult.recall)) /
                          (completeAnalysisResult.precision + completeAnalysisResult.recall),
                      )}
                      %
                    </p>
                    <p className="text-[7px] text-white/30">Balance globale</p>
                  </div>
                </div>

                {/* === 5 SCORING DIMENSIONS === */}
                <div className="p-2 bg-white/5 rounded-xl border border-white/10">
                  <p className="text-sm text-white/40 uppercase font-bold mb-2">
                    5 Dimensions du Scoring ATS
                  </p>
                  <div className="grid grid-cols-5 gap-1">
                    <div className="text-center">
                      <p className="text-[7px] text-white/30">Format</p>
                      <p className="text-sm font-bold text-blue-400">
                        {completeAnalysisResult.formatScore}/15
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-[7px] text-white/30">Keywords</p>
                      <p className="text-sm font-bold text-purple-400">
                        {completeAnalysisResult.keywordScore}/40
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-[7px] text-white/30">Experience</p>
                      <p className="text-sm font-bold text-cyan-400">
                        {completeAnalysisResult.experienceScore}/25
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-[7px] text-white/30">Contenu</p>
                      <p className="text-sm font-bold text-pink-400">
                        {completeAnalysisResult.contentScore}/12
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-[7px] text-white/30">Complet</p>
                      <p className="text-sm font-bold text-orange-400">
                        {completeAnalysisResult.completenessScore}/8
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 h-2 bg-black/30 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-green-500 transition-all"
                      style={{ width: `${completeAnalysisResult.score}%` }}
                    />
                  </div>
                  <p className="text-sm text-white/40 text-center mt-1">
                    Score total: {completeAnalysisResult.score}/100
                  </p>
                </div>

                {/* === FIELD-LEVEL METRICS === */}
                <div className="p-2 bg-white/5 rounded-xl border border-white/10">
                  <p className="text-sm text-white/40 uppercase font-bold mb-2">
                    Métriques par Champ (P/R/F1)
                  </p>
                  <div className="space-y-1">
                    {Object.entries(completeAnalysisResult.fieldMetrics).map(
                      ([field, metrics]) => (
                        <div
                          key={field}
                          className="flex items-center justify-between p-1.5 bg-black/20 rounded-lg"
                        >
                          <span className="text-sm text-white/60 capitalize w-16">{field}</span>
                          <div className="flex gap-2 text-sm">
                            <span className="text-blue-400">P:{metrics.precision}%</span>
                            <span className="text-purple-400">R:{metrics.recall}%</span>
                            <span
                              className={`font-bold ${
                                metrics.status === 'excellent'
                                  ? 'text-green-400'
                                  : metrics.status === 'good'
                                    ? 'text-green-500'
                                    : metrics.status === 'acceptable'
                                      ? 'text-yellow-400'
                                      : metrics.status === 'weak'
                                        ? 'text-orange-400'
                                        : 'text-red-400'
                              }`}
                            >
                              F1:{metrics.f1}%
                            </span>
                          </div>
                          <span
                            className={`text-[7px] px-1.5 py-0.5 rounded ${
                              metrics.status === 'excellent'
                                ? 'bg-green-500/20 text-green-400'
                                : metrics.status === 'good'
                                  ? 'bg-emerald-500/20 text-green-500'
                                  : metrics.status === 'acceptable'
                                    ? 'bg-yellow-500/20 text-yellow-400'
                                    : metrics.status === 'weak'
                                      ? 'bg-orange-500/20 text-orange-400'
                                      : 'bg-red-500/20 text-red-400'
                            }`}
                          >
                            {metrics.status}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                </div>

                {/* === INDUSTRY COMPARISON === */}
                <div className="p-2 bg-white/5 rounded-xl border border-white/10">
                  <p className="text-sm text-white/40 uppercase font-bold mb-2">
                    📊 Comparaison Industrie (Benchmarks)
                  </p>
                  <div className="space-y-1">
                    {completeAnalysisResult.industryComparison.map((comp, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between p-1 bg-black/20 rounded"
                      >
                        <span className="text-sm text-white/50 w-24">{comp.tool}</span>
                        <span className="text-sm text-white/30">{comp.f1}% F1</span>
                        <span
                          className={`text-sm font-bold ${
                            comp.status === 'above'
                              ? 'text-green-400'
                              : comp.status === 'equal'
                                ? 'text-yellow-400'
                                : 'text-red-400'
                          }`}
                        >
                          {comp.delta > 0 ? '+' : ''}
                          {comp.delta}%
                        </span>
                        <span
                          className={`text-[7px] ${
                            comp.status === 'above'
                              ? 'text-green-400'
                              : comp.status === 'equal'
                                ? 'text-yellow-400'
                                : 'text-red-400'
                          }`}
                        >
                          {comp.status === 'above' ? '↑' : comp.status === 'equal' ? '≈' : '↓'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* === RED / YELLOW / GREEN FLAGS === */}
                {completeAnalysisResult.redFlags.length > 0 && (
                  <div className="p-2 bg-red-500/10 rounded-xl border border-red-500/20">
                    <p className="text-sm text-red-400 uppercase font-bold mb-1">
                      🔴 Red Flags (Critiques)
                    </p>
                    {completeAnalysisResult.redFlags.map((flag, i) => (
                      <p key={i} className="text-[9px] text-red-300/80 leading-tight">
                        • {flag}
                      </p>
                    ))}
                  </div>
                )}

                {completeAnalysisResult.yellowFlags.length > 0 && (
                  <div className="p-2 bg-yellow-500/10 rounded-xl border border-yellow-500/20">
                    <p className="text-sm text-yellow-400 uppercase font-bold mb-1">
                      🟡 Yellow Flags (À surveiller)
                    </p>
                    {completeAnalysisResult.yellowFlags.map((flag, i) => (
                      <p key={i} className="text-[9px] text-yellow-300/80 leading-tight">
                        • {flag}
                      </p>
                    ))}
                  </div>
                )}

                {completeAnalysisResult.greenLights.length > 0 && (
                  <div className="p-2 bg-green-500/10 rounded-xl border border-green-500/20">
                    <p className="text-sm text-green-400 uppercase font-bold mb-1">
                      🟢 Green Lights (Points forts)
                    </p>
                    {completeAnalysisResult.greenLights.slice(0, 6).map((light, i) => (
                      <p key={i} className="text-[9px] text-green-300/80 leading-tight">
                        • {light}
                      </p>
                    ))}
                  </div>
                )}

                {/* === ERROR ANALYSIS === */}
                {(completeAnalysisResult.errorAnalysis.extractionMiss.length > 0 ||
                  completeAnalysisResult.errorAnalysis.formatError.length > 0) && (
                  <div className="p-2 bg-white/5 rounded-xl border border-white/10">
                    <p className="text-sm text-white/40 uppercase font-bold mb-2">
                      🔍 Analyse des Erreurs
                    </p>
                    {completeAnalysisResult.errorAnalysis.extractionMiss.length > 0 && (
                      <div className="mb-2">
                        <p className="text-[7px] text-orange-400 uppercase mb-1">
                          Champs manquants
                        </p>
                        {completeAnalysisResult.errorAnalysis.extractionMiss.map((err, i) => (
                          <p key={i} className="text-sm text-white/50">
                            • {err}
                          </p>
                        ))}
                      </div>
                    )}
                    {completeAnalysisResult.errorAnalysis.formatError.length > 0 && (
                      <div>
                        <p className="text-[7px] text-red-400 uppercase mb-1">Erreurs de format</p>
                        {completeAnalysisResult.errorAnalysis.formatError.map((err, i) => (
                          <p key={i} className="text-sm text-white/50">
                            • {err}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* === ADDITIONAL METRICS === */}
                <div className="grid grid-cols-4 gap-1">
                  <div className="bg-black/30 p-2 rounded-lg text-center">
                    <p className="text-[7px] text-white/30">Keywords</p>
                    <p className="text-sm font-bold text-cyan-400">
                      {completeAnalysisResult.keywordCoverage}%
                    </p>
                  </div>
                  <div className="bg-black/30 p-2 rounded-lg text-center">
                    <p className="text-[7px] text-white/30">Verbes</p>
                    <p className="text-sm font-bold text-purple-400">
                      {completeAnalysisResult.actionVerbCount}
                    </p>
                  </div>
                  <div className="bg-black/30 p-2 rounded-lg text-center">
                    <p className="text-[7px] text-white/30">Chiffres</p>
                    <p className="text-sm font-bold text-pink-400">
                      {completeAnalysisResult.quantifiedResults}
                    </p>
                  </div>
                  <div className="bg-black/30 p-2 rounded-lg text-center">
                    <p className="text-[7px] text-white/30">Années exp.</p>
                    <p className="text-sm font-bold text-orange-400">
                      ~{completeAnalysisResult.experienceYears}
                    </p>
                  </div>
                </div>

                {/* === DETECTED SECTIONS === */}
                {completeAnalysisResult.detectedSections.length > 0 && (
                  <div className="p-2 bg-white/5 rounded-lg border border-white/5">
                    <p className="text-sm text-white/40 uppercase font-bold mb-1">
                      Sections ATS détectées
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {completeAnalysisResult.detectedSections.map((s, i) => (
                        <span
                          key={i}
                          className="text-sm bg-green-500/20 text-green-300 px-1.5 py-0.5 rounded capitalize font-bold"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* === RECOMMENDATIONS === */}
                {completeAnalysisResult.recommendations.length > 0 && (
                  <div className="p-2 bg-blue-500/10 rounded-xl border border-blue-500/20">
                    <p className="text-sm text-blue-400 uppercase font-bold mb-1">
                      💡 Recommandations
                    </p>
                    {completeAnalysisResult.recommendations.slice(0, 5).map((r, i) => (
                      <p key={i} className="text-[9px] text-blue-300/80 leading-tight mb-1">
                        • {r}
                      </p>
                    ))}
                  </div>
                )}

                {/* === EXTRACTED TEXT PREVIEW === */}
                {completeAnalysisResult.extractedText && (
                  <details className="group">
                    <summary className="text-sm text-white/40 uppercase font-bold cursor-pointer hover:text-white/60">
                      Aperçu du texte extrait ▸
                    </summary>
                    <div className="mt-2 p-2 bg-black/30 rounded-lg border border-white/5 max-h-[500px] overflow-y-auto">
                      <p className="text-sm text-white/40 font-mono whitespace-pre-wrap leading-relaxed">
                        {completeAnalysisResult.extractedText}
                      </p>
                    </div>
                  </details>
                )}

                {/* === "AI parse to fill CV" button === */}
                {lastExtractedText.trim().length > 0 && (
                  <button
                    type="button"
                    onClick={() => void onAiParseClick()}
                    disabled={isAiParsing}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-violet-500/30 bg-blue-500/10 hover:bg-blue-500/20 text-violet-300 text-sm font-bold transition-all disabled:opacity-50"
                  >
                    {isAiParsing ? (
                      <>
                        <Loader2 size={14} className="animate-spin" /> Extraction en cours...
                      </>
                    ) : (
                      <>
                        <Sparkles size={14} /> Parser avec l'IA (remplir le CV)
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});
